import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

export type UploadTask = {
  id: string
  name?: string
  file?: File
  videoUrl?: string
  episodeUrl?: string
  progress: number
  status:
    | 'pending'
    | 'uploading'
    | 'transcribing'
    | 'completed'
    | 'error'
    | 'cancelled'
  error?: string
  submissionId?: string
  type: 'upload' | 'youtube' | 'rss'
  projectId?: number
  userId?: string
  cancel?: () => void
  toastId?: string
}

type UploadStore = {
  uploads: UploadTask[]
  showInsufficientCreditsModal: boolean
  addUploads: (uploads: UploadTask[]) => void
  removeUpload: (id: string) => void
  updateUpload: (id: string, updates: Partial<UploadTask>) => void
  updateProgress: (
    id: string,
    progress: number | ((prev: number) => number)
  ) => void
  updateStatus: (
    id: string,
    status: UploadTask['status'],
    error?: string
  ) => void
  cancelUpload: (id: string) => void
  setShowInsufficientCreditsModal: (shown: boolean) => void
}

export const useUploadStore = create<UploadStore>()(
  devtools((set) => ({
    uploads: [],
    showInsufficientCreditsModal: false,
    addUploads: (uploads) =>
      set((state) => ({ uploads: [...state.uploads, ...uploads] })),
    removeUpload: (id) =>
      set((state) => ({
        uploads: state.uploads.filter((upload) => upload.id !== id),
      })),
    cancelUpload: (id) =>
      set((state) => ({
        uploads: state.uploads.map((upload) =>
          upload.id === id ? { ...upload, status: 'cancelled' } : upload
        ),
      })),
    updateUpload: (id, updates) =>
      set((state) => ({
        uploads: state.uploads.map((upload) =>
          upload.id === id ? { ...upload, ...updates } : upload
        ),
      })),
    updateProgress: (id, progressOrUpdater) =>
      set((state) => ({
        uploads: state.uploads.map((upload) => {
          if (upload.id === id) {
            const newProgress =
              typeof progressOrUpdater === 'function'
                ? progressOrUpdater(upload.progress)
                : progressOrUpdater
            return { ...upload, progress: newProgress }
          }
          return upload
        }),
      })),
    updateStatus: (id, status, error) =>
      set((state) => ({
        uploads: state.uploads.map((upload) =>
          upload.id === id ? { ...upload, status, error } : upload
        ),
      })),
    setShowInsufficientCreditsModal: (shown) =>
      set((state) => ({
        showInsufficientCreditsModal: shown,
      })),
  }))
)

export function getErrorMessage(error: any): string {
  if (error instanceof Error) {
    if (error.message.startsWith('Unexpected token')) {
      return 'Invalid URL. Please check the URL and try again.'
    }
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return 'An unexpected error occurred.'
}
